import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateTireSpecList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/tire-spec/search');
};

export const useTireSpecDataList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue, excludeIds = []) => {
  const isFetchAll = page === undefined;

  const companies = (selectedCompanies || []).map((c) => c.value).join(',');
  const start = isFetchAll ? 0 : page * rowsPerPage;

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .addJoin('company', ['name'])
      .addJoin('tire_brand', ['name', 'code', 'code_internal'])
      .addJoin('tire_size', ['code', 'code_internal'])
      .addJoin('vehicle_type', ['name'])
      .addJoin('construction_type', ['name'])
      .setOrder(['pattern'], true);

    if (searchKey && searchValue) {
      if (searchKey === 'company') {
        fb.like('name', searchValue, 'AND', 'company');
      } else if (searchKey === 'tire_brand') {
        fb.like('name', searchValue, 'AND', 'tire_brand');
      } else if (searchKey === 'tire_size') {
        fb.like('code_internal', searchValue, 'OR', 'tire_size');
        fb.like('code', searchValue, 'OR', 'tire_size');
      } else if (searchKey === 'all') {
        fb.like('spec_name', searchValue, 'OR');
        fb.like('name', searchValue, 'OR', 'tire_brand');
        fb.like('code', searchValue, 'OR', 'tire_size');
        fb.like('code_internal', searchValue, 'OR', 'tire_size');
        fb.like('pattern', searchValue, 'OR');
        fb.like('status', searchValue, 'OR');
      } else {
        fb.like(searchKey, searchValue, 'AND');
      }
    }

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    if (excludeIds && excludeIds.length > 0) {
      fb.where('not in', 'id', excludeIds.join(','));
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companies, isFetchAll, excludeIds]);

  const key = useMemo(() => ['/tire-spec/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return {
    ...normalizeApiResponse(data, error, isLoading),
    refetch: () => mutate(key)
  };
};

export const getTireSpecList = async () => {
  try {
    const fb = new FilterBuilder()
      .setPaging(0, 9999)
      .addJoin('company', ['name'])
      .addJoin('tire_brand', ['name', 'code', 'code_internal'])
      .addJoin('tire_size', ['code', 'code_internal'])
      .addJoin('vehicle_type', ['name'])
      .addJoin('construction_type', ['name'])
      .setOrder(['pattern'], true)
      .filterByCompany();

    const payload = fb.build();
    const response = await fleetApi.post(`/tire-spec/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? [];
    return Array.isArray(dataArr) ? dataArr : [];
  } catch (error) {
    console.error('Failed to fetch tire specs:', error);
    return [];
  }
};

export const getTireSpecById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.get(`/tire-spec/${id}${getTzParam()}`);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : dataArr || null;
    return result;
  } catch (error) {
    console.error('Failed to fetch tire spec by ID:', error);
    throw error;
  }
};

export const createTireSpec = async (formData = {}) => {
  try {
    const body = { ...formData };
    const response = await fleetApi.post(`/tire-spec/new${getTzParam()}`, body);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireSpecList();
    return response;
  } catch (error) {
    console.error('Error creating tire spec:', error);
    throw error;
  }
};

export const updateTireSpec = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const response = await fleetApi.put(`/tire-spec/edit/${id}${getTzParam()}`, body);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireSpecList();
    return response;
  } catch (error) {
    console.error('Error updating tire spec:', error);
    throw error;
  }
};

export const deleteTireSpec = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/tire-spec/delete/${id}${getTzParam()}`);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireSpecList();
    return response;
  } catch (error) {
    console.error('Error deleting tire spec:', error);
    throw error;
  }
};
